import { Link } from "react-router-dom";

import SharedHeader from "@/components/SharedHeader";
import Footer from "@/components/Footer";
import { WHATSAPP_REGION_AVAILABILITY } from "@/lib/messaging";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function TermsPage() {
  return (
    <div className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}>
      <SharedHeader />

      <main className="flex-1 mx-auto max-w-3xl px-6 py-16 pt-24">
        <article className="space-y-8">
          <header className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-black mb-4 text-gray-900 dark:text-white">
              Terms of Service
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Last updated: July 19, 2026
            </p>
          </header>

          {/* PART 1: GENERAL TERMS */}
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white border-b-3 border-gray-900 dark:border-gray-300 pb-3 mb-6">
              Part 1: General Terms
            </h2>
          </div>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              These Terms of Service (&quot;Terms&quot;) govern your access to and use of the
              Podwise GPT website, applications, and services (collectively, the
              &quot;Service&quot;). By creating an account or using the Service, you agree to
              these Terms.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              1. What Podwise GPT Provides
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Podwise GPT is a digital software service that helps you browse, analyze, and
              interact with podcast content using AI. We transcribe podcast episodes and
              enable you to ask questions through supported websites, apps, APIs, MCP
              clients, and messaging channels. We do not ship physical goods. Access is
              delivered online through your account after payment is authorized.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              2. Accounts and Access
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                You are responsible for activity under your account, including activity
                performed through an MCP token, connected WhatsApp account, or other client you
                authorize.
              </li>
              <li>
                You agree not to share login links or credentials, disclose an MCP token to
                an untrusted party, or resell access to the Service.
              </li>
              <li>
                We may suspend or limit access if we detect abuse, fraud, or behavior that
                risks the stability of the Service.
              </li>
              <li>
                You must provide accurate information when creating your account and keep
                it up to date.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              3. Acceptable Use
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You agree to use the Service only for lawful purposes. You may not:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                Use the Service to violate any applicable laws or regulations.
              </li>
              <li>
                Attempt to gain unauthorized access to the Service or its related systems.
              </li>
              <li>
                Interfere with or disrupt the integrity or performance of the Service.
              </li>
              <li>
                Use automated systems to access the Service except through an interface we
                expressly provide, such as the API or MCP endpoint, and within its limits.
              </li>
              <li>
                Upload or transmit malicious code, viruses, or harmful content.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              4. Intellectual Property
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              The Service, including its original content, features, and functionality, is
              owned by Podwise GPT and protected by copyright, trademark, and other
              intellectual property laws. Podcast content remains the property of its
              respective creators; we only provide tools to help you interact with that
              content.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              4A. Signal, Telegram, WhatsApp, and Connected Clients
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                Signal access is optional. You must explicitly consent before we link a
                Signal identity to your Podwise account. You may send STOP at any time and
                may unlink the Signal account from Settings.
              </li>
              <li>
                Telegram access is optional. You must explicitly consent before we link a
                Telegram identity to your Podwise account. Signup and payment take place on
                the Podwise website, not in Telegram. You may send STOP at any time and may
                unlink the Telegram account from Settings.
              </li>
              <li>
                WhatsApp access is optional. You must explicitly consent before we link a
                WhatsApp identity to your Podwise account. You may send STOP or another
                recognized opt-out keyword at any time, and may unlink the WhatsApp account
                from Settings.
              </li>
              <li>
                {WHATSAPP_REGION_AVAILABILITY}
              </li>
              <li>
                Podwise uses Signal as a transport for Signal messages. Signal, internet,
                device, or network failures may delay or prevent delivery. Carrier or data
                charges from your provider may apply.
              </li>
              <li>
                Podwise uses Telegram&apos;s Bot API to transport Telegram messages. Telegram,
                internet, device, or network failures may delay or prevent delivery. Internet
                or data charges from your provider may apply.
              </li>
              <li>
                Podwise uses Twilio and Meta to deliver WhatsApp messages. Their systems may
                delay, block, reclassify, or fail to deliver a message. Carrier, internet, or
                data charges from your provider may apply.
              </li>
              <li>
                Messaging answers are limited to podcasts followed by the connected Podwise
                account. Signal and Telegram may split a long answer across several messages. WhatsApp
                message length and Meta&apos;s customer-service window may require an approved
                message template.
              </li>
              <li>
                Podwise is not an emergency service. AI-generated answers may be incomplete
                or inaccurate and must not be relied on as medical, legal, financial, safety,
                or other professional advice.
              </li>
              <li>
                MCP tokens act like passwords and may grant broad tool access, including
                billable questions and session management. You are responsible for revoking
                a token or unlinking a messaging channel you no longer trust.
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Our handling of Signal, Telegram, and WhatsApp account-routing information, messages, conversation context, and provider
              retention is described in the{" "}
              <Link to="/privacy" className={cn("font-semibold hover:underline", theme.text.primaryOrange)}>
                Privacy Policy
              </Link>.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              5. Disclaimer of Warranties
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of
              any kind, either express or implied. We do not guarantee that the Service
              will be uninterrupted, secure, or error-free. AI-generated responses are
              based on podcast transcripts and may not be perfectly accurate.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              6. Limitation of Liability
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              To the maximum extent permitted by law, Podwise GPT shall not be liable for
              any indirect, incidental, special, consequential, or punitive damages, or
              any loss of profits or revenues, whether incurred directly or indirectly, or
              any loss of data, use, goodwill, or other intangible losses resulting from
              your use of the Service.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              7. Changes to Terms
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may update these Terms from time to time. If we make material changes, we
              will notify you by email or by posting a notice on our website. Your
              continued use of the Service after such notice constitutes acceptance of the
              updated Terms.
            </p>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              8. Governing Law
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws
              of the jurisdiction in which Podwise GPT operates, without regard to its
              conflict of law provisions.
            </p>
          </section>

          {/* PART 2: BILLING & SUBSCRIPTION POLICY */}
          <div className="mt-16 mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white border-b-3 border-gray-900 dark:border-gray-300 pb-3 mb-6">
              Part 2: Billing &amp; Subscription Policy
            </h2>
          </div>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              9. Platform Subscription
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                We offer recurring monthly and yearly subscription plans as described on
                our Pricing page and at checkout.
              </li>
              <li>
                By starting a subscription, you authorize us to charge the payment method
                you provide for the recurring fee shown at the time of purchase, plus
                applicable taxes, on each renewal date until you cancel.
              </li>
              <li>
                Your initial charge occurs as soon as you start a subscription. Subsequent
                charges occur on the same calendar day of each billing period (or the
                closest available date if that day does not exist in a given month).
              </li>
              <li>
                Subscription plans include a set amount of prepaid credits that can be
                used for transcription and chat usage.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              10. Podcast Subscriptions (Per-Podcast Charges)
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                When you add a podcast to your account, you must choose whether to
                follow new episodes from that point forward or import the podcast&apos;s
                full back catalog.
              </li>
              <li>
                If you choose <strong>Start from now</strong>, existing episodes are not
                imported into your library and cannot be used in chat. New episodes
                published after the saved following cutoff are automatically transcribed
                and billed at the prevailing rate as they arrive.
              </li>
              <li>
                If you choose <strong>Import the full back catalog</strong>, we show an
                estimated upfront cost based on the number of existing episodes in the
                podcast&apos;s feed. The actual cost depends on audio duration and content
                complexity and may be more or less than the estimate.
              </li>
              <li>
                By confirming a full-history import, you authorize Podwise GPT to charge
                your available credits or payment method up to the estimated amount to
                transcribe existing episodes. Processing begins immediately, and those
                digital-service charges are non-refundable once processing begins.
              </li>
              <li>
                Under either history option, future episodes that fall within your saved
                policy are automatically transcribed and billed. See our Pricing page for
                current rate estimates.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              11. Question and Chat Usage
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                Answer requests made through the web app, API, MCP, Signal, Telegram, or WhatsApp consume
                credits or produce metered usage. The cost varies based on the complexity
                of your question, the content analyzed, and the interface used.
              </li>
              <li>
                See our Pricing page for current estimated rates.
              </li>
              <li>
                Signal is included as an interface for every active plan rather than sold
                as a separate add-on. The Signal transport is not added as a second Podwise
                usage charge.
              </li>
              <li>
                Telegram is included as an interface for every active plan rather than sold
                as a separate add-on. Telegram transport is not added as a second Podwise
                usage charge.
              </li>
              <li>
                WhatsApp is included as an interface for every active plan rather than sold
                as a separate add-on. Twilio and Meta transport costs may be included in the
                same chat usage charge; they do not appear as a separate subscription.
              </li>
              <li>
                Chat usage is billed from your available credits first. If your credits are
                exhausted, charges are applied to your payment method. For messaging, your plan
                must still be eligible when answer processing completes. Usage already completed
                and recorded remains billable if you cancel afterward, although Podwise may
                suppress a later provider delivery when your plan is no longer active.
              </li>
              <li>
                Because AI processing is performed on demand, consumed chat usage is
                generally non-refundable.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              12. Credits
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                Subscription plans include prepaid credits that are applied toward
                transcription and chat usage costs.
              </li>
              <li>
                Credits are deducted as you use the service. You can view your current
                credit balance in your account settings.
              </li>
              <li>
                Unused credits remain available within your billing period. Credit
                policies may vary by plan.
              </li>
              <li>
                Once credits have been consumed to process transcription or chat requests,
                they are non-refundable.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              13. Automatic Renewal
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                Subscriptions renew automatically at the end of each billing period unless
                you cancel in advance.
              </li>
              <li>
                We will send a receipt by email after each successful renewal. The receipt
                includes your plan, amount charged, and a link to manage or cancel your
                subscription.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              14. How to Cancel
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                You can cancel at any time from within the app by navigating to{" "}
                <strong>Profile &rarr; Billing</strong> and using the in-product
                cancellation button, or by emailing{" "}
                <a
                  href="mailto:quiet.lens2873@fastmail.com"
                  className={cn("font-semibold hover:underline", theme.text.primaryOrange)}
                >
                  quiet.lens2873@fastmail.com
                </a>
                .
              </li>
              <li>
                Cancellations take effect at the end of your current billing period. You
                will retain access to paid features until that date and will not be
                charged again unless you restart your subscription.
              </li>
              <li>
                You do not need to call us or speak to a human to cancel; online
                cancellation through your account is sufficient.
              </li>
              <li>
                Canceling pauses paid features at the end of the applicable access period
                but does not automatically delete your account, podcast settings, linked
                messaging account, sessions, or billing records. You may unlink Signal or WhatsApp and
                delete supported sessions independently. Contact us to request account
                deletion; legal, security, billing, provider, and backup records may be
                retained as described in the Privacy Policy.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              15. Refunds
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                Because Podwise GPT is a digital, immediately-available service,
                subscription fees, transcription charges, chat usage, and messaging
                transport usage are generally non-refundable.
              </li>
              <li>
                We may, at our discretion, issue a credit or refund for unused time on a
                current subscription period or for unused credits where we determine that
                a billing error or technical issue on our side occurred.
              </li>
              <li>
                If you believe you were billed in error, please contact us at{" "}
                <a
                  href="mailto:quiet.lens2873@fastmail.com"
                  className={cn("font-semibold hover:underline", theme.text.primaryOrange)}
                >
                  quiet.lens2873@fastmail.com
                </a>{" "}
                within 30 days so we can investigate and resolve the issue.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              16. Chargebacks &amp; Disputes
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
              <li>
                If you don&apos;t recognize a charge from us, or you believe a charge is
                incorrect, please contact us first. We can usually resolve billing issues
                more quickly than your bank.
              </li>
              <li>
                When a payment is disputed with a card issuer, access to the associated
                subscription or credits may be paused while the card issuer investigates.
              </li>
              <li>
                We may submit evidence of your account activity (such as login history,
                usage logs, transcription records, and cancellation records) to your card
                issuer in order to contest unfounded disputes.
              </li>
            </ul>
          </section>

          <section className={cn(theme.cards.base, "p-6 space-y-4")}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              17. Contact
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              For questions about these Terms or billing, please contact us at{" "}
              <a
                href="mailto:quiet.lens2873@fastmail.com"
                className={cn("font-semibold hover:underline", theme.text.primaryOrange)}
              >
                quiet.lens2873@fastmail.com
              </a>
              .
            </p>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
